import { FilterOperatorType } from '../odata-filter/models/filter-operator-type';
import { FilterUIType, ODataFilterValues } from '../odata-filter/models/odata-filter';
import { FilterOperator } from '../odata-filter/models/operators';
import { SearchValueType } from '../odata-filter/models/search-value-type';
import moment from 'moment';

/** from requirement.automations[where RequirementAutomation.automationActionType is Automation].conditions */

export interface BaseAutomationConditionModel {
  objectId: string;
  searchValue: string[];
  operator: FilterOperatorType;
  searchValueType?: SearchValueType;
}
export interface AutomationConditionModel extends BaseAutomationConditionModel {
  /** each condition needs to be uniquely identified. Doesn't really matter if it's a feeDefinitionId or just a Guid.NewGuid() */
  id?: string;
  subConditions?: BaseAutomationConditionModel[];
}
export interface AutomationConditionItem {
  objectId: string;
  displayName: string;
  values: any[] | ODataFilterValues[] | null;
  valueType: SearchValueType;
  subConditionItems?: AutomationConditionItem[];
}

export class SelectedCondition {
  id: string;
  filter: AutomationConditionItem;
  selectedSubConditions: SelectedCondition[] = [];
  filterOperator: FilterOperator;
  searchValue?: any = null;
  searchValues?: ODataFilterValues[] = [];
  isFilterApplied = false;
  showFieldDeletedWarning = false;
  constructor(data: any) {
    if (!data) return;
    this.id = data.id;
    this.filter = data.filter;
    this.filterOperator = data.filterOperator;
    this.searchValue = data.searchValue ?? null;
    this.searchValues = data.searchValues ?? [];
    this.showFieldDeletedWarning = !data.filter;
    this.isFilterApplied = data.isFilterApplied;
  }

  get isValid(): boolean {
    return (
      this.filter &&
      this.filterOperator &&
      ((this.filterOperator.isSearchValueNeeded &&
        (this.searchValues.length > 0 ||
          (this.searchValue != null && this.searchValue.toString() != ''))) ||
        !this.filterOperator.isSearchValueNeeded)
    );
  }

  get uiType(): FilterUIType {
    if (!this.filter) return null;
    switch (this.filter.valueType) {
      case SearchValueType.List:
      case SearchValueType.Boolean:
        return FilterUIType.DropDown;
      case SearchValueType.MultiValue:
      case SearchValueType.EvaluationAggregateMultiValue:
      case SearchValueType.WorkspaceMultiValue:
      case SearchValueType.EmptyOperator:
        return FilterUIType.Multiselect;
      case SearchValueType.Skill:
        return this.filterOperator.actualOperator == FilterOperatorType.contains
          ? FilterUIType.Textbox
          : FilterUIType.Multiselect;
      case SearchValueType.Date:
      case SearchValueType.DateRange:
        return FilterUIType.DatePicker;
      case SearchValueType.String:
      case SearchValueType.Email:
      case SearchValueType.Number:
      case SearchValueType.EvaluationAggregateString:
        return FilterUIType.Textbox;
    }
  }

  get hasSubFilter(): boolean {
    return this.filter && this.filter.subConditionItems?.length > 0;
  }

  get isSubFilterAdded(): boolean {
    return this.selectedSubConditions.length > 0;
  }

  get isSubFilterValid(): boolean {
    return this.selectedSubConditions.findIndex((x) => x.isFilterApplied && !x.isValid) == -1;
  }

  get isSubFilerApplied(): boolean {
    return this.selectedSubConditions.findIndex((x) => !x.isFilterApplied) == -1;
  }

  get appliedSubFilters(): SelectedCondition[] {
    return this.selectedSubConditions.filter((x) => x.isValid).map((x) => new SelectedCondition(x));
  }

  /** to make sure we do not send any value if filter uses an operator that does not need value. */
  private getSearchValue = (): string[] => {
    if (this.filterOperator && !this.filterOperator.isSearchValueNeeded) {
      return [];
    }
    if (this.uiType == FilterUIType.Multiselect) {
      return this.searchValues.map((x) => x.searchValue);
    }
    if (this.searchValue) {
      const filterType = this.filter?.valueType;
      if (filterType == SearchValueType.DateRange || filterType == SearchValueType.Date) {
        if (moment.isMoment(this.searchValue)) {
          const momentDate = this.searchValue as moment.Moment;
          return [momentDate.format('YYYY-MM-DD')]; //backend converts to DateOnly and gets confused if we add send time component
        }
      }
      return [this.searchValue];
    }
    return (this.searchValues || []).map((x) => x.searchValue);
  };

  /** result of this is sent to API */
  buildConditionModel = (
    includeSubConditions: boolean = true
  ): AutomationConditionModel | BaseAutomationConditionModel => {
    const baseModel = {
      id: this.id,
      objectId: this.filter.objectId,
      searchValue: this.getSearchValue(),
      operator: this.filterOperator.displayOperator,
      searchValueType: this.filter.valueType
    };
    if (includeSubConditions) {
      return {
        ...baseModel,
        subConditions: this.appliedSubFilters.map((x) => x.buildConditionModel(false))
      } as AutomationConditionModel;
    }
    return baseModel as BaseAutomationConditionModel;
  };
}
